import React, { Dispatch, useEffect, useMemo, useState } from 'react';

import { PlayType, PlayTypeNameEnum } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Alert from '@amzn/meridian/alert';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import RadioButton from '@amzn/meridian/radio-button';
import Text from '@amzn/meridian/text';

import { isEmpty, isUndefined } from 'lodash';
import { ContractTermSection } from 'src/components/shared/ContractTermSection';
import { ServiceId } from 'src/constants';
import { IPrimeAMFRateCardValidationAction } from 'src/types/IPrimeAMFRateCardValidationAction';
import { IPrimeAMFRateCardValuesAction } from 'src/types/IPrimeAMFRateCardValuesAction';
import { IPrimeAMFRateCardValues } from 'src/types/IRateCard';

import { IPrimeAMFRateCardErrors } from '../../rateCardValidationReducer';

interface IAddPlayType {
    rateCardValues: IPrimeAMFRateCardValues;
    dispatchRateCardValues: Dispatch<IPrimeAMFRateCardValuesAction>;
    rateCardErrors: IPrimeAMFRateCardErrors;
    dispatchRateCardErrors: Dispatch<IPrimeAMFRateCardValidationAction>;
    playTypes: PlayType[];
}

export const AddPlayType: React.FC<IAddPlayType> = ({
    rateCardValues,
    dispatchRateCardValues,
    rateCardErrors,
    dispatchRateCardErrors,
    playTypes
}) => {
    const isPrime = rateCardValues.serviceId === ServiceId.Prime;
    const rateCardPlayTypes = isPrime
        ? rateCardValues.primeContractTerms?.costPerPlay?.playTypes
        : rateCardValues.amfContractTerms?.adRevShare?.playTypes;
    const hasPlayTypes = !isEmpty(rateCardPlayTypes);
    const onDemandDefinitions = useMemo(() => {
        const onDemandPlayType = playTypes.find((playType) => playType.playTypeName === PlayTypeNameEnum.ON_DEMAND);
        return isUndefined(onDemandPlayType)
            ? []
            : [...onDemandPlayType.playTypeDefinitions!].sort(
                  (d1, d2) => d1.playTypeDefinitionId! - d2.playTypeDefinitionId!
              );
    }, [playTypes]);
    const [isToggled, setIsToggled] = useState(hasPlayTypes);
    const errorMessage = isPrime
        ? rateCardErrors.primeContractTerms.costPerPlay.playTypes
        : rateCardErrors.amfContractTerms.adRevShare.playTypes;

    // Validate on rate card/toggle change
    useEffect(() => {
        dispatchRateCardErrors({
            type: 'validate_play_type',
            rateCardValues,
            isToggled
        });
    }, [
        rateCardValues.primeContractTerms?.costPerPlay?.playTypes,
        rateCardValues.amfContractTerms?.adRevShare.playTypes
    ]);

    const containsPlayType = (playTypeName: string): boolean =>
        rateCardPlayTypes?.some((pt) => pt.playTypeName === playTypeName) ?? false;

    const isOnDemandDefinitionChecked = (id: number): boolean => {
        if (!containsPlayType(PlayTypeNameEnum.ON_DEMAND)) {
            return false;
        }

        const definition = rateCardPlayTypes?.filter(
            (playType) => playType.playTypeName === PlayTypeNameEnum.ON_DEMAND
        )[0].playTypeDefinitions;

        return definition?.length === 1 && definition[0].playTypeDefinitionId === id;
    };

    const handleToggle = (): void => {
        if (isToggled) {
            dispatchRateCardValues({ type: 'reset_play_types' });
        }

        setIsToggled((prevIsToggled) => !prevIsToggled);
    };

    const handlePlayTypeChange = (playTypeId: number, shouldBeChecked: boolean): void => {
        dispatchRateCardValues({
            type: 'set_play_type',
            playType: playTypes.filter((playType) => playType.playTypeId === playTypeId)[0],
            shouldBeIncluded: shouldBeChecked
        });
    };

    const handleOnDemandDefinitionChange = (onDemandDefinitionId: number): void => {
        dispatchRateCardValues({
            type: 'set_on_demand_definition',
            onDemandDefinition: onDemandDefinitions!.filter(
                (definition) => definition.playTypeDefinitionId === onDemandDefinitionId
            )[0]
        });
    };

    return (
        <ContractTermSection
            title="Play type"
            subTitle="Enable different rates per play type (On-Demand, Shuffle, Station, etc.)"
            isToggled={isToggled}
            handleToggle={handleToggle}
        >
            {isToggled && (
                <>
                    <Column spacing="300">
                        <Text type="h300">Select play types</Text>
                        {playTypes
                            .filter((playType) =>
                                Object.keys(PlayTypeNameEnum)
                                    .filter((playTypeName) => playTypeName !== PlayTypeNameEnum.STANDARD)
                                    .includes(playType.playTypeName!)
                            )
                            .map((playType) => (
                                <Checkbox
                                    key={playType.playTypeName}
                                    data-testid={playType.playTypeName}
                                    checked={containsPlayType(playType.playTypeName!)}
                                    onChange={(val) => handlePlayTypeChange(playType.playTypeId!, val)}
                                >
                                    {playType.playTypeText}
                                </Checkbox>
                            ))}
                    </Column>
                    {containsPlayType(PlayTypeNameEnum.ON_DEMAND) && (
                        <Column spacing="300">
                            <Text type="h300">On-Demand playlist calculation</Text>
                            {onDemandDefinitions?.map((definition) => (
                                <RadioButton
                                    key={definition.playTypeDefinitionId}
                                    data-testid={definition.playTypeDefinitionName}
                                    checked={isOnDemandDefinitionChecked(definition.playTypeDefinitionId!)}
                                    onChange={() => handleOnDemandDefinitionChange(definition.playTypeDefinitionId!)}
                                >
                                    {definition.description}
                                </RadioButton>
                            ))}
                        </Column>
                    )}
                    {!isEmpty(errorMessage) && (
                        <Alert type="error" size="small">
                            {errorMessage}
                        </Alert>
                    )}
                </>
            )}
        </ContractTermSection>
    );
};
